import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import nonDigitalSteps from '../../assets/data/non-digital-steps.json';
import digitalSteps from '../../assets/data/steps.json';
import { FormType } from '../config/constants';
import { RequestService } from './request/request.service';

interface Step {
  id: string;
  heading: string;
  flowId: string;
  substeps: SubStep[];
  active: boolean;
  completed: boolean;
  disabled?: boolean;
}

interface SubStep {
  id: string;
  heading: string;
  route: string;
  flowId: string;
  active: boolean;
  completed: boolean;
  disabled?: boolean;
}

@Injectable({ providedIn: 'root' })
export class HeaderSharedService {
  private _lastStep = false;
  private _activeFlow = 'UIMUI';
  private _moreDetails = false;

  private stepsSubject = new BehaviorSubject<Step[]>([]);
  public steps$ = this.stepsSubject.asObservable();
  public steps: Step[] = [];

  public activeIndex = 0;
  public childActiveIndex = 0;
  public consentSelection = '';
  public flowModes: { [key: string]: 'digital' | 'non-digital' } = {
    uimui: 'digital',
    pip: 'digital',
  };

  constructor(private requestService: RequestService) {
    this.initializeSteps();
  }

  get lastStep(): boolean {
    return this._lastStep;
  }

  set lastStep(value: boolean) {
    this._lastStep = value;
  }

  get moreDetails(): boolean {
    return this._moreDetails;
  }

  set moreDetails(value: boolean) {
    this._moreDetails = value;
  }

  get activeFlow(): string {
    return this._activeFlow;
  }

  set activeFlow(value: string) {
    this._activeFlow = value;
  }

  private initializeSteps() {
    let initialSteps: Step[] = digitalSteps.map((step) => ({
      id: step.flowId,
      heading: step.heading,
      flowId: step.flowId,
      substeps: step.substeps.map((substep) => ({
        id:
          substep.flowId +
          '-' +
          substep.heading.toLowerCase().replace(/ /g, '-'),
        heading: substep.heading,
        route: substep.route,
        /*
        route:
          substep.flowId +
          '-' +
          substep.heading.toLowerCase().replace(/ /g, '-'),
          */
        flowId: substep.flowId,
        active: false,
        completed: false,
      })),
      active: step.flowId === 'uimui',
      completed: false,
    }));
    let requestFormTypeList = this.requestService.requestFormTypeList;
    if (
      requestFormTypeList.length === 1 &&
      requestFormTypeList[0] === FormType.UM_UIM
    ) {
      initialSteps = initialSteps.filter((step) => step.flowId === 'uimui');
    } else if (
      requestFormTypeList.length === 1 &&
      requestFormTypeList[0] === FormType.PIP
    ) {
      initialSteps = initialSteps.filter((step) => step.flowId === 'pip');
    }

    this.stepsSubject.next(initialSteps);
    this.steps = initialSteps;
  }

  switchToNonDigital(flow: 'uimui' | 'pip') {
    this.consentSelection = 'non-digital';
    this.flowModes[flow] = 'non-digital';
    const currentSteps = this.stepsSubject.getValue();
    const updatedSteps = currentSteps.map((step) => {
      if (step.flowId === flow) {
        const nonDigitalSubsteps =
          nonDigitalSteps.find((s) => s.flowId === flow)?.substeps || [];
        return {
          ...step,
          substeps: nonDigitalSubsteps.map((substep) => ({
            id: substep.id,
            heading: substep.heading,
            route: substep.route,
            flowId: substep.flowId,
            active: substep.active,
            completed: substep.completed,
          })),
        };
      }
      return step;
    });
    this.stepsSubject.next(updatedSteps);
    this.steps = updatedSteps;
  }

  revertToDigital(flow: 'uimui' | 'pip') {
    this.flowModes[flow] = 'digital';
    const digitalSubsteps =
      digitalSteps.find((s) => s.flowId === flow)?.substeps || [];
    const currentSteps = this.stepsSubject.getValue();
    const updatedSteps = currentSteps.map((step) => {
      if (step.flowId === flow) {
        return {
          ...step,
          substeps: digitalSubsteps.map((substep) => ({
            id:
              substep.flowId +
              '-' +
              substep.heading.toLowerCase().replace(/ /g, '-'),
            heading: substep.heading,
            route: substep.route,
            flowId: substep.flowId,
            active: false,
            completed: false,
          })),
        };
      }
      return step;
    });
    this.stepsSubject.next(updatedSteps);
    this.steps = updatedSteps;
  }

  markUIMUICompleted() {
    const currentSteps = this.stepsSubject.getValue();
    const isNonDigital = this.flowModes['uimui'] === 'non-digital';
    const updatedSteps = currentSteps.map((step) => {
      if (step.flowId === 'uimui') {
        return {
          ...step,
          active: false,
          completed: true,
          disabled: true,
          substeps: step.substeps.map((substep) => ({
            ...substep,
            active: false,
            completed: true,
            disabled: true,
          })),
        };
      }
      return { ...step, active: step.flowId === 'pip' };
    });
    this.stepsSubject.next(updatedSteps);
    this.steps = updatedSteps;
    this.activeFlow = 'PIP';
    this.activeIndex = 1;
    this.childActiveIndex = 0;
  }

  markPIPCompleted() {
    const currentSteps = this.stepsSubject.getValue();
    const updatedSteps = currentSteps.map((step) => {
      if (step.flowId === 'pip') {
        return {
          ...step,
          active: false,
          completed: true,
          disabled: true,
          substeps: step.substeps.map((substep) => ({
            ...substep,
            active: false,
            completed: true,
            disabled: true,
          })),
        };
      }
      return { ...step, active: false };
    });
    this.stepsSubject.next(updatedSteps);
    this.steps = updatedSteps;
    this.activeFlow = 'PIP';
    this.activeIndex = this.steps.findIndex((step) => step.flowId === 'pip');
    this.childActiveIndex = -1;
  }

  updateStepState(url: string) {
    let route = url.split('/').pop() || '';
    route = route.split('?')[0];

    const currentFlow = url.includes('pip') ? 'pip' : 'uimui';
    const routeToHeadingMap: Record<string, string> = {
      'uimui-consent': 'Digital Signature Consent',
      'uimui-selection': 'Selection of Coverages',
      'uimui-review-state': 'Review State Selections',
      'uimui-outstanding-state': 'Outstanding State Selections',
      'uimui-signature': 'Electronic Signature',
      'uimui-non-digital': 'Non-Digital Elections',
      'pip-consent': 'Digital Signature Consent',
      'pip-selection': 'Selection of Coverages',
      'pip-review-state': 'Review State Selections',
      'pip-outstanding-state': 'Outstanding State Selections',
      'pip-signature': 'Electronic Signature',
      'pip-non-digital': 'Non-Digital Elections',
      'uimui-thankyou': '',
      'pip-thankyou': '',
    };
    const matchedHeading = routeToHeadingMap[route];
    const currentSteps = this.stepsSubject.getValue();
    const isNonDigitalUIMUI = this.flowModes['uimui'] === 'non-digital';

    const updatedSteps = currentSteps.map((step) => {
      // Apply to BOTH digital & non-digital flows
      if (route === 'uimui-thankyou' && step.flowId === 'uimui') {
        return {
          ...step,
          active: false,
          completed: true,
          substeps: step.substeps.map((substep) => ({
            ...substep,
            active: false,
            completed: true,
          })),
        };
      }
      if (route === 'pip-thankyou' && step.flowId === 'pip') {
        return {
          ...step,
          active: false,
          completed: true,
          substeps: step.substeps.map((substep) => ({
            ...substep,
            active: false,
            completed: true,
          })),
        };
      }
      if (route.match('uimui-consent') || route.match('pip-consent')) {
        this.moreDetails = false;
      } else {
        this.moreDetails = true;
      }

      if (
        step.flowId !== currentFlow &&
        route !== 'uimui-thankyou' &&
        route !== 'pip-thankyou'
      ) {
        return step; // Don't update substeps of the other flow
      }

      const activeIndex = matchedHeading
        ? step.substeps.findIndex((sub) => sub.heading === matchedHeading)
        : -1;

      return {
        ...step,
        active:
          url.includes(step.flowId) &&
          route !== 'uimui-thankyou' &&
          route !== 'pip-thankyou',
        substeps: step.substeps.map((sub, index) => ({
          ...sub,
          active: index === activeIndex,
          completed:
            index < activeIndex || this.checkrouteStep(route, step.flowId, sub),
        })),
        completed: step.substeps.every((sub) => sub.completed),
      };
    });

    this.stepsSubject.next(updatedSteps);
    this.steps = updatedSteps;
    this.activeIndex = updatedSteps.findIndex((step) => step.active);
    this.childActiveIndex =
      updatedSteps[this.activeIndex]?.substeps.findIndex((sub) => sub.active) ??
      0;
  }

  /**
   * Reset subsequent steps when form values change (AC2)
   * Marks all substeps after the current active substep as incomplete
   * @param flow - The flow to reset ('pip' or 'uimui')
   */
  resetSteps(flow: 'pip' | 'uimui') {
    const currentSteps = this.stepsSubject.getValue();
    const updatedSteps = currentSteps.map((step) => {
      if (step.flowId === flow) {
        const activeSubstepIndex = step.substeps.findIndex((sub) => sub.active);
        return {
          ...step,
          substeps: step.substeps.map((substep, index) => ({
            ...substep,
            // Mark substeps after active as incomplete
            completed: index <= activeSubstepIndex ? substep.completed : false,
          })),
        };
      }
      return step;
    });
    this.stepsSubject.next(updatedSteps);
    this.steps = updatedSteps;
  }

  checkrouteStep(route: string, flowId: string, sub: any) {
    if (
      (route.includes('uimui-thankyou') && flowId === 'uimui') ||
      (route.includes('pip-thankyou') && flowId === 'pip')
    ) {
      return true;
    }
    return sub.completed;
  }
}
